import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton, Divider,
  RadioGroup, FormControlLabel, Radio, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { SketchPicker } from 'react-color';
import { frameOfCondition } from '../utils/framePrefix';

function fmtWin(c) {
  if (!c.windowMode || !c.nDays) return '';
  if (c.windowMode === 'around') return `(${c.nDays}d before & after)`;
  if (c.windowMode === 'after')  return `(${c.nDays}d after)`;
  return `(${c.nDays}d before)`;
}

function describeCondition(c) {
  switch (c.type) {
    // Group 1 – Sidereal Zodiac Criteria
    case 'at_longitude_in_sign':       return `${c.planet} at longitude ${c.longitude} in sign ${c.sign} ${fmtWin(c)}`.trim();
    case 'declination_difference':     return `${c.planet1} – ${c.planet2} declination difference ${c.diff} ${fmtWin(c)}`.trim();
    case 'longitude_difference':       return `${c.planet1} – ${c.planet2} longitude difference ${c.diff} ${fmtWin(c)}`.trim();
    case 'at_longitude':               return `${c.planet} at longitude ${c.longitude} ${fmtWin(c)}`.trim();
    case 'at_declination':             return `${c.planet} at declination ${c.declination} ${fmtWin(c)}`.trim();
    case 'ingress_rashi':              return `${c.planet} ingress Rashi/Sign ${c.sign}`;
    case 'ingress_navamsha':           return `${c.planet} ingress Navamsha Sign ${c.sign}`;
    case 'ingress_nakshatra':          return `${c.planet} ingress Nakshatra ${c.nakshatra}`;
    case 'combust_in_east':            return `${c.planet} combust in the ${c.hemisphere}`;
    case 'rising':                     return `${c.planet} rising in the ${c.hemisphere}`;
    case 'retrograde':                 return `${c.planet} retrograde in the ${c.hemisphere}`;
    case 'direct':                     return `${c.planet} direct in the ${c.hemisphere}`;
    case 'declination_more_than_sun':  return `${c.planet} declination more than Sun`;
    case 'g3_out_of_bounds':           return `${c.planet} Out Of Bounds (declination > 23.45\u00b0)`;
    case 'g3_sun_conjunction':         return `Sun and ${c.planet} ${c.conjType} conjunction ${fmtWin(c)}`;
    // Group 2 – Tropical & Heliocentric
    case 'tropical_at_longitude_in_sign': return `${c.planet} at longitude ${c.longitude} in sign ${c.sign} (Tropical)`;
    case 'tropical_ingress_rashi':     return `${c.planet} ingress Rashi/Sign ${c.sign} (Tropical)`;
    case 'tropical_at_longitude':      return `${c.planet} at longitude ${c.longitude} (Tropical)`;
    case 'geo_conjunction_phase': {
      // The phase is named only when one is being filtered on, so an unfiltered row reads as the
      // plain conjunction it is rather than claiming a state it never checked.
      const head = (!c.phase || c.phase === 'Any') ? 'Conjunction' : `${c.phase} conjunction`;
      const frame = c.frame === 'sidereal' ? 'Sidereal' : 'Tropical';
      return `${head} of ${c.planet1} with ${c.planet2} within ${c.orb}° (${frame})`;
    }
    case 'harmonic':                   return `Harmonic ${c.harmonic} of ${c.planet} (offset ${c.startingOffset}, ${c.forPercent}%)`;
    case 'helio_longitude_difference': return `Helio: ${c.planet1} – ${c.planet2} longitude difference ${c.diff}`;
    case 'helio_at_longitude':         return `Helio: ${c.planet} at longitude ${c.longitude}`;
    // Group 3 – Vedic Planetary Geometry
    case 'vedic_follows_by_signs':     return `Vedic: ${c.planet1} follows ${c.planet2} by ${c.numSigns} signs`;
    case 'vedic_apart_by_signs':       return `Vedic: ${c.planet1} and ${c.planet2} apart by ${c.numSigns} signs`;
    case 'vedic_apart_by_nakshatra':   return `Vedic: ${c.planet1} and ${c.planet2} apart by ${c.numNakshatra} nakshatra`;
    case 'vedic_aspected_by':          return `Vedic: ${c.planet1} aspected by ${c.planet2} (${c.aspectType === 'all' ? 'all aspects' : '7th aspect'})`;
    case 'vedic_in_sign':              return `Vedic: ${c.planet1} in ${c.sign1} and ${c.planet2} in ${c.sign2}`;
    case 'vedic_in_nakshatra':         return `Vedic: ${c.planet1} in ${c.nakshatra1} and ${c.planet2} in ${c.nakshatra2}`;
    case 'vedic_midpoint':             return `Vedic: ${c.planet1} at midpoint of ${c.planet2} and ${c.planet3} (\u00b1${c.tolerance}\u00b0)`;
    // Group 4 – Sidereal Zodiac (extended)
    case 'g4_harmonic_planet':         return `Harmonic ${c.harmonic} of ${c.planet} (offset ${c.offsetDeg}\u00b0, within ${c.withinHarmonic}\u00b0)`;
    case 'g4_decl_diff_range':         return `${c.planet1} – ${c.planet2} declination diff ${c.fromDecDiff}\u00b0 to ${c.toDeg}\u00b0`;
    case 'g4_decl_range':              return `${c.planet} declination ${c.fromDecl}\u00b0 to ${c.toDecl}\u00b0`;
    case 'g4_distance_category':       return `${c.planet} distance category ${c.distCat}`;
    case 'g4_speed_category':          return `${c.planet} speed category ${c.speedCat}`;
    case 'g4_nakshatra_charan':        return `${c.planet1} & ${c.planet2} in nakshatra charan ${c.charan} within ${c.withinN}`;
    case 'g4_in_rashi':                return `${c.planet1} & ${c.planet2} in rashi ${c.sign}`;
    case 'g4_in_nakshatra':            return `${c.planet1} & ${c.planet2} in nakshatra ${c.nakshatra}`;
    case 'g4_longitude_range':         return `${c.planet} longitude ${c.fromDeg}\u00b0 to ${c.toDeg}\u00b0`;
    case 'g4_longi_diff_range':        return `${c.planet1} – ${c.planet2} longitude diff ${c.fromDeg}\u00b0 to ${c.toDeg}\u00b0`;
    case 'g4_bars_range':              return `${c.planet1} & ${c.planet2} at ${c.atDeg}\u00b0 (${c.barsBefore} bars before to ${c.barsAfter} bars after)`;
    case 'g4_rahu_ketu_between':       return 'All planets between Rahu and Ketu';
    case 'g4_ketu_rahu_between':       return 'All planets between Ketu and Rahu';
    case 'g4_moon_status':             return `Moon ${c.status}`;
    case 'g4_mercury_status':          return `Mercury ${c.status}`;
    // Group 5 – Planet State
    case 'g5_planet_state':            return `${c.planet} is ${c.state}`;
    case 'g5_day_is':                  return `Day is ${c.day} ${fmtWin(c)}`;
    case 'g5_month_is':                return `Month is ${c.month}`;
    case 'g5_paksha_tithi':            return `${c.paksha} Paksha, ${c.tithi} ${fmtWin(c)}`;
    case 'g5_weekday':                 return `Weekday ${c.weekday} ${fmtWin(c)}`;
    case 'g5_yog':                     return `Yog ${c.yog} ${fmtWin(c)}`;
    case 'g5_weekday_yog':             return `Weekday ${c.weekday} and Yog ${c.yog}`;
    case 'g5_vedic_month':             return `Vedic month ${c.vedicMonth}`;
    case 'g5_day_lord_aspect':         return `Day lord ${c.dayLord} aspects ${c.planet} at ${c.aspectDeg}\u00b0 ${fmtWin(c)}`;
    case 'g5_day_lord_ingress':        return `Day lord ${c.dayLord} ingress in ${c.sign} ${fmtWin(c)}`;
    // Group 6 – Aspects & Positional
    case 'g6_follows_by_signs':        return `${c.planet1} follows ${c.planet2} by ${c.numSigns} signs`;
    case 'g6_apart_by_signs':          return `${c.planet1} & ${c.planet2} apart by ${c.numSigns} signs`;
    case 'g6_apart_by_nakshatra':      return `${c.planet1} & ${c.planet2} apart by ${c.numNakshatra} nakshatra`;
    case 'g6_aspected_by_planet':      return `${c.planet1} aspected by ${c.planet2} (${c.aspectType === 'all' ? 'all aspects' : '7th aspect'})`;
    case 'g6_aspecting_sign':          return `${c.planet} aspecting ${c.sign} (${c.aspectType === 'all' ? 'all aspects' : '7th aspect'})`;
    case 'g6_planet_in_sign':          return `${c.planet} in ${c.sign}`;
    case 'g6_planet_in_nakshatra':     return `${c.planet} in ${c.nakshatra}`;
    case 'g6_planet_sign_and_planet_sign': return `${c.planet1} in ${c.sign1} and ${c.planet2} in ${c.sign2}`;
    case 'g6_planet_naksh_and_planet_naksh': return `${c.planet1} in ${c.naksh1} and ${c.planet2} in ${c.naksh2}`;
    case 'g6_midpoint':                return `${c.planet1} at midpoint of ${c.planet2} and ${c.planet3} (\u00b1${c.tolerance}\u00b0)`;
    case 'g6_conjunct_lunar_eclipse':  return `${c.planet} conjunct most recent Lunar eclipse`;
    case 'g6_conjunct_solar_eclipse':  return `${c.planet} conjunct most recent Solar eclipse`;
    // Group 7 – Natal Degree Aspects
    case 'g7_conjunct_natal':          return `${c.planet1} conjunct natal ${c.planet2}`;
    case 'g7_opposite_natal':          return `${c.planet1} opposite natal ${c.planet2}`;
    case 'g7_conjunct_rising_longitude':   return `${c.planet1} conjunct Rising Longitude`;
    case 'g7_conjunct_midheaven_longitude': return `${c.planet1} conjunct Midheaven Longitude`;
    case 'g7_in_rising_sign':          return `${c.planet1} in Rising Sign`;
    case 'g7_in_midheaven_sign':       return `${c.planet1} in Midheaven Sign`;
    default:                           return JSON.stringify(c);
  }
}

/**
 * The same description, with every planet name carrying its frame tag.
 *
 * Applied over the finished string rather than inside all eighty cases above. Each case builds its
 * sentence from the condition's own fields, so the planet names are exactly the values of
 * `planet`, `planet1`, `planet2`, `planet3` and `dayLord` — substituting those is one rule instead
 * of eighty edits, and a case added later is tagged without anyone remembering to do it.
 *
 * Longest name first, so "Mars" inside a hypothetical longer name cannot be replaced out from
 * under it, and each occurrence is replaced only once — a description naming the same planet twice
 * ("Sun and Sun") would otherwise have the tag applied to an already-tagged name.
 */
export function describeConditionWithFrame(c) {
  const text = describeCondition(c);
  const tag = frameOfCondition(c);
  if (!tag) return text;

  const names = [c.planet, c.planet1, c.planet2, c.planet3, c.dayLord]
    .filter(n => typeof n === 'string' && n.length > 0)
    .filter((n, i, all) => all.indexOf(n) === i)
    .sort((a, b) => b.length - a.length);

  let out = text;
  names.forEach(name => {
    // Word boundaries so "Sun" in "Sunday" is left alone, and a negative lookbehind for the dot so
    // a name already tagged by an earlier pass is not tagged twice.
    out = out.replace(new RegExp(`(?<![.\\w])${name}\\b`, 'g'), `${tag}.${name}`);
  });
  return out;
}

const NumInput = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)}
    inputProps={{ min: 0, step: 1 }} sx={{ width: 70 }} />
);
NumInput.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const MOAExecuteTab = ({
  conditions, onDeleteCondition, onDeleteAllConditions,
  timeWindowMode, onTimeWindowModeChange,
  twNBefore, onTwNBeforeChange,
  twNAfter, onTwNAfterChange,
  twNAround1, onTwNAround1Change,
  twNAround2, onTwNAround2Change,
  selMode, onSelModeChange,
  exactVal, onExactValChange,
  gteVal, onGteValChange,
  lteVal, onLteValChange,
  rangeGte, onRangeGteChange,
  rangeLte, onRangeLteChange,
  color, onColorChange,
  result,
}) => {
  const setSelMode = onSelModeChange;
  const setExactVal = onExactValChange;
  const setGteVal = onGteValChange;
  const setLteVal = onLteValChange;
  const setRangeGte = onRangeGteChange;
  const setRangeLte = onRangeLteChange;
  const setColor = onColorChange;

  return (
    <Box sx={{ p: 2.5 }}>

      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography fontWeight="bold" fontSize={17}>
          Selected Strategy Components
        </Typography>
        {conditions.length > 0 && (
          <Button variant="outlined" color="error" size="small"
            sx={{ fontSize: 13 }}
            onClick={onDeleteAllConditions}>
            Delete All
          </Button>
        )}
      </Box>

      {/* Always-visible conditions list box */}
      <Box sx={{ border: '1px solid #bbb', height: 160, overflowY: 'auto', mb: 3 }}>
        {conditions.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#aaa' }}>
            <Typography fontSize={15}>No conditions added yet. Go to the criteria tabs, from Transit through Origin++, to add them.</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {conditions.map((c, idx) => (
              <React.Fragment key={idx}>
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={`${describeConditionWithFrame(c)}: value ${c.value}, otherwise 0`}
                    primaryTypographyProps={{ fontSize: 15 }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => onDeleteCondition(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {idx < conditions.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Time Window Row */}
      <Box sx={{ border: '1px solid #bbb', borderRadius: 1, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>

          {/* Option 1: N days before event */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
            <Radio
              checked={timeWindowMode === 'before'}
              onChange={() => onTimeWindowModeChange('before')}
              size="small"
              sx={{ p: 0.5 }}
            />
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>From</Typography>
            <NumInput value={twNBefore} onChange={onTwNBeforeChange} />
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>Days to the event</Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Option 2: N days after event */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
            <Radio
              checked={timeWindowMode === 'after'}
              onChange={() => onTimeWindowModeChange('after')}
              size="small"
              sx={{ p: 0.5 }}
            />
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>From event</Typography>
            <NumInput value={twNAfter} onChange={onTwNAfterChange} />
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>days after</Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Option 3: N1 before to N2 after */}
          <Box sx={{ flex: 2, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
            <Radio
              checked={timeWindowMode === 'around'}
              onChange={() => onTimeWindowModeChange('around')}
              size="small"
              sx={{ p: 0.5 }}
            />
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>From</Typography>
            <NumInput value={twNAround1} onChange={onTwNAround1Change} />
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>days before the event to</Typography>
            <NumInput value={twNAround2} onChange={onTwNAround2Change} />
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>days after the event</Typography>
          </Box>

        </Box>
      </Box>

      {/* Ready to execute — header full width, then two columns */}
      <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
        Ready to execute strategy?
      </Typography>
      <Typography fontSize={14} sx={{ mb: 0.5, color: '#444' }}>
        Select the highlight color and the criteria for selection and click execute.
      </Typography>
      <Typography fontSize={14} sx={{ mb: 2.5, color: '#444' }}>
        Depending upon the number of iterations required and complexity, it can take a few seconds or a couple of minutes to complete
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 5, width: '100%' }}>

        {/* LEFT: criteria box */}
        <Box sx={{ flex: '0 0 auto' }}>
          <Box sx={{ border: '2px solid #333', borderRadius: 1, p: 3, minWidth: 420 }}>
            <RadioGroup value={selMode} onChange={e => setSelMode(e.target.value)}>

              <FormControlLabel
                value="exact"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '1.05rem', whiteSpace: 'nowrap' }}>Value exactly =</Typography>
                    <NumInput value={exactVal} onChange={setExactVal} />
                  </Box>
                }
                sx={{ mb: 1.5 }}
              />

              <FormControlLabel
                value="gte"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '1.05rem', whiteSpace: 'nowrap' }}>Value &gt;=</Typography>
                    <NumInput value={gteVal} onChange={setGteVal} />
                  </Box>
                }
                sx={{ mb: 1.5 }}
              />

              <FormControlLabel
                value="lte"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '1.05rem', whiteSpace: 'nowrap' }}>Value &lt;=</Typography>
                    <NumInput value={lteVal} onChange={setLteVal} />
                  </Box>
                }
                sx={{ mb: 1.5 }}
              />

              <FormControlLabel
                value="range"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '1.05rem', whiteSpace: 'nowrap' }}>Value &gt;=</Typography>
                    <NumInput value={rangeGte} onChange={setRangeGte} />
                    <Typography sx={{ fontSize: '1.05rem', whiteSpace: 'nowrap' }}>and &lt;=</Typography>
                    <NumInput value={rangeLte} onChange={setRangeLte} />
                  </Box>
                }
              />

            </RadioGroup>
          </Box>
        </Box>

        {/* RIGHT: color picker */}
        <Box sx={{ flex: '0 0 auto' }}>
          <Typography fontWeight="bold" fontSize={15} sx={{ mb: 1.5 }}>Select highlight color</Typography>
          <Box sx={{ width: 220, height: 32, backgroundColor: color, border: '1px solid #bbb', borderRadius: 1, mb: 1 }} />
          <SketchPicker color={color} onChangeComplete={c => setColor(c.hex)} />
        </Box>

      </Box>

      {/* Result summary */}
      {result && (
        <Box sx={{ mt: 3, p: 2, border: '1px solid #bbb', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
          <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
            Execution Result
          </Typography>
          <Typography fontSize={14}>
            Days evaluated: <b>{result.totalDaysEvaluated}</b> &nbsp;|&nbsp;
            Matching dates: <b>{result.totalMatches}</b>
          </Typography>
          {result.matchingDates && result.matchingDates.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 160, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1, backgroundColor: '#fff' }}>
              {result.matchingDates.map((m, i) => (
                <Typography key={i} fontSize={13} sx={{ fontFamily: 'monospace' }}>
                  {m.date} — {m.totalPoints} pts
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

    </Box>
  );
};

MOAExecuteTab.propTypes = {
  conditions: PropTypes.array.isRequired,
  onDeleteCondition: PropTypes.func.isRequired,
  onDeleteAllConditions: PropTypes.func.isRequired,
  timeWindowMode: PropTypes.oneOf(['before', 'after', 'around', null]),
  onTimeWindowModeChange: PropTypes.func.isRequired,
  twNBefore: PropTypes.string.isRequired,
  onTwNBeforeChange: PropTypes.func.isRequired,
  twNAfter: PropTypes.string.isRequired,
  onTwNAfterChange: PropTypes.func.isRequired,
  twNAround1: PropTypes.string.isRequired,
  onTwNAround1Change: PropTypes.func.isRequired,
  twNAround2: PropTypes.string.isRequired,
  onTwNAround2Change: PropTypes.func.isRequired,
  selMode: PropTypes.string.isRequired,
  onSelModeChange: PropTypes.func.isRequired,
  exactVal: PropTypes.string.isRequired,
  onExactValChange: PropTypes.func.isRequired,
  gteVal: PropTypes.string.isRequired,
  onGteValChange: PropTypes.func.isRequired,
  lteVal: PropTypes.string.isRequired,
  onLteValChange: PropTypes.func.isRequired,
  rangeGte: PropTypes.string.isRequired,
  onRangeGteChange: PropTypes.func.isRequired,
  rangeLte: PropTypes.string.isRequired,
  onRangeLteChange: PropTypes.func.isRequired,
  color: PropTypes.string.isRequired,
  onColorChange: PropTypes.func.isRequired,
  result: PropTypes.object,
};

export default MOAExecuteTab;
